import type { Observable } from "rxjs";
import { combineLatest, filter, forkJoin, take, tap } from "rxjs";

import type { RewardsInjectorDefinition } from "../../types";

class RewardsWidgetInjectionService {
    private injectors = <RewardsInjectorDefinition[]>[];

    constructor(private readonly isDesignMode: boolean | null) {}

    private executeInjector(
        conditions: Array<Observable<boolean>>,
        startInjector: () => void,
        combineFunction: typeof forkJoin | typeof combineLatest
    ): void {
        combineFunction(conditions)
            .pipe(
                filter((conditions) => conditions.every(Boolean)),
                take(1),
                tap(() => startInjector())
            )
            .subscribe();
    }

    private injectWidgetsInDesignMode(): void {
        this.injectors.forEach(
            ({ injectInDesignMode, designModeConditions, startInjector }) => {
                if (!injectInDesignMode) return;

                if (designModeConditions) {
                    this.executeInjector(
                        designModeConditions,
                        startInjector,
                        forkJoin
                    );
                } else {
                    startInjector();
                }
            }
        );
    }

    public registerInjectors(injectors: RewardsInjectorDefinition[]) {
        this.injectors = injectors;
    }

    public injectWidgets(): void {
        if (this.isDesignMode) {
            this.injectWidgetsInDesignMode();

            return;
        }

        this.injectors.forEach(({ shopConditions, startInjector }) => {
            combineLatest(shopConditions)
                .pipe(
                    filter((observable) => observable.every(Boolean)),
                    take(1),
                    tap(() => startInjector())
                )
                .subscribe();
        });
    }
}

export default RewardsWidgetInjectionService;
